import { useContext, useEffect, useState, createContext } from 'react'
import { useSnackbar } from 'notistack'
import EmbeddedLogin from './EmbeddedLogin'
import { useAuth } from '../../services/AuthProvider/AuthProvider'
import StorageManager from '../../services/StorageManager'
import { LOGGED_OUT_PARAM } from '../../services/LoginUtills'
import { SNACKBAR_VARIANTS } from '../../elements/Snackbar/Snackbar.consts'
import { translateManager as t } from '../../services/TranslateManager'
import { isClient } from 'utils/runtimeUtils'
import NoSSR from 'utils/NoSSR'
import { useLocation, useSearchParams } from 'react-router'

const ONE_TIME_LOGIN_DISPLAYED = 'oneTimeLoginDisplayed'

type OpenParams = {
  onLoginProcessStart?: () => void
  source?: string
  skipRefresh?: boolean
}

type EmbeddedLoginContextType = {
  open: (params: OpenParams) => void
  openOnce: (params: OpenParams) => boolean
  isOpen: boolean
}

const EmbeddedLoginContext = createContext<
  EmbeddedLoginContextType | undefined
>(undefined)

export function useEmbeddedLogin() {
  const context = useContext(EmbeddedLoginContext)
  if (!context) {
    throw new Error('useEmbeddedLogin must be used within a SideMenuProvider')
  }

  return context
}

export default function EmbeddedLoginProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [onLoginProcessStart, setOnLoginProcessStart] = useState<
    () => void | undefined
  >(() => {})
  const [source, setSource] = useState<string>('')
  const [skipRefresh, setSkipRefresh] = useState<boolean>(false)
  const { isAuthenticated } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [loginDisplayed, setLoginDisplayed] = useState(() =>
    isClient() ? StorageManager.get<boolean>(ONE_TIME_LOGIN_DISPLAYED) : false
  )

  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const loggedOutParam = searchParams.get(LOGGED_OUT_PARAM)

  useEffect(() => {
    setIsOpen(false)
  }, [location])

  useEffect(() => {
    if (!loggedOutParam) return
    setSearchParams(
      (params) => {
        params.delete(LOGGED_OUT_PARAM)
        return params
      },
      { replace: true }
    )
    enqueueSnackbar('', {
      variant: SNACKBAR_VARIANTS.COMMON,
      title: t.get('logout.signed_out_successfully'),
      subtitle: t.get('logout.signed_out_successfully_subtitle'),
      name: 'Logout Success',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedOutParam])

  const open = ({
    onLoginProcessStart = () => {},
    source = '',
    skipRefresh = false,
  }: OpenParams) => {
    setOnLoginProcessStart(onLoginProcessStart)
    setSource(source)
    setSkipRefresh(skipRefresh)
    setIsOpen(true)
  }

  const openOnce = ({
    onLoginProcessStart = () => {},
    source = '',
    skipRefresh = false,
  }: OpenParams) => {
    if (!isAuthenticated && !loginDisplayed) {
      open({
        onLoginProcessStart,
        source,
        skipRefresh,
      })
      StorageManager.set(ONE_TIME_LOGIN_DISPLAYED, true)
      setLoginDisplayed(true)
      return true
    }
    return false
  }

  const onDismiss = () => {
    setIsOpen(false)
    StorageManager.set(ONE_TIME_LOGIN_DISPLAYED, false)
    setLoginDisplayed(false)
  }

  return (
    <EmbeddedLoginContext.Provider value={{ open, openOnce, isOpen }}>
      <NoSSR>
        <EmbeddedLogin
          open={isOpen}
          onDismiss={() => onDismiss()}
          onLoginProcessStart={onLoginProcessStart}
          source={source}
          skipRefresh={skipRefresh}
        />
      </NoSSR>
      {children}
    </EmbeddedLoginContext.Provider>
  )
}
